import React, { useRef } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { LogoUploadViewModel } from "@/features/settings/viewmodel/LogoUploadViewModel";
import "./LogoUpload.scss";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { imageList } from "@/core/utils/imageList";

interface LogoUploadProps {
  src: string;
  onChange: (fid: string) => void;
}

export const LogoUpload: React.FC<LogoUploadProps> = ({ src, onChange }) => {
  const {
    fileInputRef,
    logoUrl,
    onUploadClick,
    onFileChange,
    isUploading,
    error,
  } = LogoUploadViewModel(src);

  const lastNotifiedFid = useRef<string>("");

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const fid = await onFileChange(e);
      if (fid && fid !== lastNotifiedFid.current) {
        lastNotifiedFid.current = fid;
        onChange(fid);
      }
    } catch (err) {
      console.error("File upload failed:", err);
    }
  };

  const hasValidLogo =
    logoUrl && !(Array.isArray(logoUrl) && logoUrl.length === 0);

  return (
    <div className="logo-upload-wrapper">
      <div className="logo-upload flex align-items-center gap-4">
        <div className="logo-container" style={{ position: "relative" }}>
          {hasValidLogo ? (
            <img
              className="logo-preview"
              src={logoUrl}
              alt="Organization logo"
            />
          ) : (
            <div className="logo-preview placeholder">
              <BgImage img={imageList.noOrgimage} />
            </div>
          )}

          {isUploading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                borderRadius: "8px",
              }}
            >
              <ProgressSpinner
                style={{ width: "40px", height: "40px" }}
                strokeWidth="4"
              />
            </div>
          )}
        </div>

        <div className="logo-actions">
          <button
            type="button"
            className="btn-upload"
            onClick={onUploadClick}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Click to upload"}
          </button>

          {error && <p className="upload-error">{error}</p>}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
          disabled={isUploading}
        />
      </div>
    </div>
  );
};
